import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  FormControlLabel,
  Button,
  Box,
  Radio,
  RadioGroup,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import dayjs from 'dayjs';
import { INSURANCES } from '../../config/insurances';
import { computeResults, reasonOptions } from './computeResults';

function TransportationDialog({
  open,
  dialogMode,
  formData,
  setFormData,
  onClose,
  onCreateOrUpdate,
  onDelete,
  facilityLocation,
  facilitySettings,
}) {
  const {
    transportationType,
    residentName,
    date,
    time,
    destination,
    primaryInsurance,
    secondaryInsurance,
    reason,
    medicallyNecessary,
    roundTrip,
    selectedOption: formSelectedOption,
  } = formData;

  const [destinationInput, setDestinationInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [drivingDistance, setDrivingDistance] = useState(null);
  const [drivingDuration, setDrivingDuration] = useState(null);
  const [resultStatus, setResultStatus] = useState('');
  const [resultOptions, setResultOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(
    formSelectedOption || '',
  );

  const adjustedDistance =
    drivingDistance !== null
      ? roundTrip
        ? drivingDistance * 2
        : drivingDistance
      : null;
  const adjustedDuration =
    drivingDuration !== null
      ? roundTrip
        ? drivingDuration * 2
        : drivingDuration
      : null;

  const showReasonSelect =
    transportationType === 'stretcher' &&
    (primaryInsurance === 'Medicare(Skilled)' ||
      primaryInsurance === 'MED A HMO(Skilled)');

  const destinationInitialized = useRef(false);
  useEffect(() => {
    if (!destinationInitialized.current && destination && destination.address) {
      setDestinationInput(destination.address);
      destinationInitialized.current = true;
    }
  }, [destination]);

  useEffect(() => {
    handleChange('destination', {
      ...destination,
      address: destinationInput,
    });
    if (
      destinationInput &&
      destinationInput.length > 3 &&
      window.google &&
      window.google.maps &&
      window.google.maps.places &&
      window.google.maps.places.AutocompleteSuggestion
    ) {
      const fetchSuggestions = async () => {
        try {
          const sessionToken =
            new window.google.maps.places.AutocompleteSessionToken();
          const request = { input: destinationInput, sessionToken };
          const { suggestions: newSuggestions } =
            await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
              request,
            );
          setSuggestions(newSuggestions);
        } catch (error) {
          console.error('Error fetching suggestions:', error);
        }
      };
      fetchSuggestions();
    } else {
      setSuggestions([]);
    }
  }, [destinationInput]);

  useEffect(() => {
    const computeDrivingDistance = async () => {
      if (
        facilityLocation?.lat != null &&
        facilityLocation?.lng != null &&
        destination?.lat != null &&
        destination?.lng != null &&
        typeof window !== 'undefined'
      ) {
        const payload = {
          origin: {
            location: {
              latLng: {
                latitude: facilityLocation.lat,
                longitude: facilityLocation.lng,
              },
            },
          },
          destination: {
            location: {
              latLng: {
                latitude: destination.lat,
                longitude: destination.lng,
              },
            },
          },
          travelMode: 'DRIVE',
          routingPreference: 'TRAFFIC_AWARE',
          languageCode: 'en-US',
          units: 'IMPERIAL',
        };
        try {
          const response = await fetch(
            `https://routes.googleapis.com/directions/v2:computeRoutes?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Goog-FieldMask': 'routes.distanceMeters,routes.duration',
              },
              body: JSON.stringify(payload),
            },
          );
          const data = await response.json();
          if (data?.routes && data.routes.length > 0) {
            const route = data.routes[0];
            let distanceMeters = null;
            let durationStr = null;
            if (route.legs && route.legs.length > 0) {
              distanceMeters = route.legs[0].distanceMeters;
              durationStr = route.legs[0].duration;
            } else if (route.distanceMeters) {
              distanceMeters = route.distanceMeters;
              durationStr = route.duration;
            }
            if (distanceMeters != null) {
              setDrivingDistance(distanceMeters / 1609.34);
            } else {
              console.warn('No distance found in route:', route);
              setDrivingDistance(null);
            }
            if (durationStr) {
              const durationSeconds = Number(durationStr.replace('s', ''));
              setDrivingDuration(durationSeconds / 60);
            } else {
              console.warn('No duration found in route:', route);
              setDrivingDuration(null);
            }
          } else {
            console.warn('No routes returned:', data);
            setDrivingDistance(null);
            setDrivingDuration(null);
          }
        } catch (error) {
          console.error(
            'Error computing driving distance with Routes API:',
            error,
          );
          setDrivingDistance(null);
          setDrivingDuration(null);
        }
      }
    };

    computeDrivingDistance();
  }, [facilityLocation, destination]);

  const computeOptionText = useCallback(
    (opt) => {
      const computedCost =
        adjustedDistance !== null
          ? (opt.baseCost + adjustedDistance * opt.perMile).toFixed(2)
          : opt.baseCost;
      return `${opt.number}. ${opt.label} ($${computedCost})`;
    },
    [adjustedDistance],
  );

  useEffect(() => {
    if (drivingDistance === null) return;

    const { status, facilityOptions } = computeResults(
      formData,
      facilitySettings,
    );
    setResultStatus(status);
    setResultOptions(facilityOptions);

    if (selectedOption) {
      const validOptionStrings = facilityOptions.map((opt) =>
        computeOptionText(opt),
      );
      if (!validOptionStrings.includes(selectedOption)) {
        setSelectedOption('');
        handleChange('selectedOption', '');
      }
    }
  }, [
    drivingDistance,
    transportationType,
    primaryInsurance,
    secondaryInsurance,
    medicallyNecessary,
    facilitySettings,
    destination,
    reason,
    roundTrip,
    selectedOption,
    formData,
    computeOptionText,
  ]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const areResultFieldsFilled = () => {
    const requiredFields = [date, time, destination?.address, primaryInsurance];
    const allStringFieldsFilled = requiredFields.every(
      (field) => field && field !== '',
    );
    const medicallyNecessaryFilled = typeof medicallyNecessary === 'boolean';
    const roundTripFilled = typeof roundTrip === 'boolean';
    return allStringFieldsFilled && medicallyNecessaryFilled && roundTripFilled;
  };

  const areRequiredFieldsFilled = () => {
    return areResultFieldsFilled() && selectedOption && selectedOption !== '';
  };

  const handleSubmit = () => {
    if (!areRequiredFieldsFilled()) {
      window.alert('Please fill out all required fields.');
      return;
    }
    onCreateOrUpdate();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {dialogMode === 'edit'
          ? 'Edit Transportation Appointment'
          : 'New Transportation Appointment'}
      </DialogTitle>
      <DialogContent dividers>
        <Select
          fullWidth
          value={transportationType}
          onChange={(e) => handleChange('transportationType', e.target.value)}
          sx={{ mb: 2 }}
        >
          <MenuItem value="wheelchair">Wheelchair</MenuItem>
          <MenuItem value="stretcher">Stretcher</MenuItem>
        </Select>

        <TextField
          label="Resident Name"
          fullWidth
          required
          value={residentName}
          onChange={(e) => handleChange('residentName', e.target.value)}
          sx={{ mb: 2 }}
        />

        <TextField
          label="Date"
          type="date"
          fullWidth
          required
          InputLabelProps={{ shrink: true }}
          value={date.format('YYYY-MM-DD')}
          onChange={(e) => handleChange('date', dayjs(e.target.value))}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Time"
          type="time"
          fullWidth
          required
          InputLabelProps={{ shrink: true }}
          value={time.format('HH:mm')}
          onChange={(e) => {
            const [hours, minutes] = e.target.value.split(':').map(Number);
            handleChange('time', time.hour(hours).minute(minutes));
          }}
          sx={{ mb: 2 }}
        />

        <Autocomplete
          freeSolo
          options={suggestions}
          getOptionLabel={(option) =>
            typeof option === 'string'
              ? option
              : option.placePrediction.text.text
          }
          inputValue={destinationInput}
          onInputChange={(event, newInputValue) => {
            setDestinationInput(newInputValue);
          }}
          onChange={async (event, selectedOption) => {
            if (selectedOption && typeof selectedOption !== 'string') {
              const placePrediction = selectedOption.placePrediction;
              const place = placePrediction.toPlace();
              await place.fetchFields({
                fields: ['formattedAddress', 'location'],
              });
              const address = place.formattedAddress;
              const lat = place.location.lat();
              const lng = place.location.lng();
              handleChange('destination', { address, lat, lng });
              setDestinationInput(address);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Destination"
              placeholder="Enter destination"
              fullWidth
              required
              sx={{ mb: 2 }}
            />
          )}
        />

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="primary-insurance-label">
            Primary Insurance
          </InputLabel>
          <Select
            labelId="primary-insurance-label"
            label="Primary Insurance"
            required
            value={primaryInsurance}
            onChange={(e) => handleChange('primaryInsurance', e.target.value)}
          >
            {INSURANCES.map((ins) => (
              <MenuItem key={ins} value={ins}>
                {ins}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="secondary-insurance-label">
            Secondary Insurance
          </InputLabel>
          <Select
            labelId="secondary-insurance-label"
            label="Secondary Insurance"
            value={secondaryInsurance}
            onChange={(e) => handleChange('secondaryInsurance', e.target.value)}
          >
            {['Medicaid', 'Private Pay'].map((ins) => (
              <MenuItem key={ins} value={ins}>
                {ins}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {showReasonSelect ? (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="reason-label">Reason</InputLabel>
            <Select
              labelId="reason-label"
              label="Reason"
              value={reason || ''}
              onChange={(e) => handleChange('reason', e.target.value)}
            >
              {reasonOptions.map((opt, index) => (
                <MenuItem key={index} value={opt}>
                  {`${index + 1}. ${opt}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <TextField
            label="Reason (ex. Dentist)"
            fullWidth
            value={reason || ''}
            onChange={(e) => handleChange('reason', e.target.value)}
            sx={{ mb: 2 }}
          />
        )}

        <p>Medical Necessity Form complete and sent to Transport Vendor? *</p>
        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <RadioGroup
            row
            name="medicallyNecessary"
            value={
              typeof medicallyNecessary === 'boolean'
                ? medicallyNecessary
                  ? 'yes'
                  : 'no'
                : ''
            }
            onChange={(e) =>
              handleChange('medicallyNecessary', e.target.value === 'yes')
            }
          >
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="no" control={<Radio />} label="No" />
          </RadioGroup>
        </FormControl>

        <p>Round Trip? *</p>
        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <RadioGroup
            row
            name="roundTrip"
            value={
              typeof roundTrip === 'boolean' ? (roundTrip ? 'yes' : 'no') : ''
            }
            onChange={(e) =>
              handleChange('roundTrip', e.target.value === 'yes')
            }
          >
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="no" control={<Radio />} label="No" />
          </RadioGroup>
        </FormControl>

        {resultOptions.length > 0 && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="facility-option-label">
              Facility Option *
            </InputLabel>
            <Select
              labelId="facility-option-label"
              label="Facility Option *"
              value={selectedOption}
              onChange={(e) => {
                setSelectedOption(e.target.value);
                handleChange('selectedOption', e.target.value);
              }}
            >
              {resultOptions.map((opt) => {
                const optionText = computeOptionText(opt);
                return (
                  <MenuItem key={opt.number} value={optionText}>
                    {optionText}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        )}

        <Box sx={{ mt: 2, p: 2, border: '1px solid #ccc', borderRadius: 1 }}>
          <h2>Results:</h2>
          {!areResultFieldsFilled() ? (
            <p>Incomplete data</p>
          ) : (
            <>
              <p>{resultStatus}</p>
              <p>
                Driving Distance:{' '}
                {adjustedDistance !== null
                  ? `${adjustedDistance.toFixed(2)} miles${
                      roundTrip ? ' (round trip)' : ''
                    }`
                  : 'N/A'}
              </p>
              <p>
                Duration:{' '}
                {adjustedDuration !== null
                  ? `${adjustedDuration.toFixed(2)} minutes${
                      roundTrip ? ' (round trip)' : ''
                    }`
                  : 'N/A'}
              </p>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {dialogMode === 'edit' && (
          <Button onClick={onDelete} color="error">
            Delete
          </Button>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>
          {dialogMode === 'edit' ? 'Save Changes' : 'Add Appointment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

TransportationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  dialogMode: PropTypes.oneOf(['edit', 'new']).isRequired,
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreateOrUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  facilityLocation: PropTypes.shape({
    address: PropTypes.string,
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  facilitySettings: PropTypes.object,
};

export default TransportationDialog;
